import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { SITE } from '../data/seoContent';

const videoLd = (r) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: r.title,
  description: r.description,
  thumbnailUrl: [SITE + r.poster],
  contentUrl: SITE + r.src,
  uploadDate: r.uploadDate,
  duration: r.duration,
  keywords: r.keywords.join(', '),
  inLanguage: 'en-US',
  publisher: { '@type': 'Organization', name: 'Leisure Wellness Naples', logo: { '@type': 'ImageObject', url: SITE + '/logo.png' } },
});

// Reusable self-hosted video feature (title + description + player). Pass `reel` for SEO markup.
export const VideoBlock = ({ eyebrow, title, subtitle, src, reel, testId, dark = false }) => {
  const videoRef = useRef(null);
  // Stop playback (and its audio) when this block unmounts, e.g. on navigation.
  useEffect(() => {
    const v = videoRef.current;
    return () => { try { if (v) v.pause(); } catch (e) { /* noop */ } };
  }, []);
  const heading = title || reel?.title;
  return (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="my-12 md:my-16"
    data-testid={testId}
  >
    {reel && (
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(videoLd(reel))}</script>
      </Helmet>
    )}
    {(eyebrow || heading || subtitle) && (
      <div className="text-center max-w-3xl mx-auto mb-6">
        {eyebrow && <span className="text-sm font-semibold text-[#F88200] uppercase tracking-wider">{eyebrow}</span>}
        {heading && (
          <h2 className={`font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-black uppercase leading-none ${dark ? 'text-white' : 'text-[#0E3D95]'}`}>
            {heading}
          </h2>
        )}
        {subtitle && <p className={`mt-2 ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{subtitle}</p>}
      </div>
    )}
    <div className="flex justify-center">
      <video
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        poster={reel?.poster}
        title={reel?.title}
        aria-label={reel?.title}
        className="w-auto max-w-full max-h-[80vh] rounded-2xl shadow-2xl bg-black"
        data-testid={testId ? `${testId}-player` : undefined}
      >
        <source src={reel?.src || src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
    {reel && (
      <div className="max-w-3xl mx-auto mt-5 text-center" data-testid={testId ? `${testId}-details` : undefined}>
        <p className={`text-sm md:text-base ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{reel.description}</p>
        <ul className="flex flex-wrap justify-center gap-2 mt-3" aria-label="Video topics">
          {reel.keywords.map((k) => (
            <li key={k} className={`text-xs px-3 py-1 rounded-full ${dark ? 'bg-white/10 text-slate-200' : 'bg-[#0E3D95]/5 text-[#0E3D95]'}`}>{k}</li>
          ))}
        </ul>
      </div>
    )}
  </motion.section>
  );
};

export default VideoBlock;
